import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Heart, ThumbsUp, ThumbsDown, Bookmark, Share2, ExternalLink, Megaphone, Star, Gift, Play, Pause, ChevronLeft, ChevronRight } from 'lucide-react';
import type { AcMediaItem, MediaAcaoBotao } from '../../pages/master/AcMediaMockData';
import { MOCK_FEED } from '../../pages/master/AcMediaMockData';
import { AcShieldLogo } from './AcShieldLogo';

const AD_DURATION_MS = 8000;

interface Props {
  mode: 'full' | 'compact';
  onExit?: () => void;
  sidebarWidth?: number;
  /** 0 = nunca exibir propaganda. N = exibir a cada N conteúdos. */
  propagandaInterval?: number;
  readonly?: boolean;
  animationSpeedMultiplier?: number;
  staggerDelay?: number;
}

/** Mapa de ícones para o botão de ação configurável */
const CTA_ICONS: Record<string, React.ReactNode> = {
  heart: <Heart size={18} />,
  bookmark: <Bookmark size={18} />,
  share: <Share2 size={18} />,
  link: <ExternalLink size={18} />,
  megaphone: <Megaphone size={18} />,
  star: <Star size={18} />,
  gift: <Gift size={18} />,
};

export function AcMediaPlayer({ mode, onExit, sidebarWidth, propagandaInterval = 0, readonly = false, animationSpeedMultiplier = 1, staggerDelay = 0.3 }: Props) {
  const [feed] = useState<AcMediaItem[]>(MOCK_FEED);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [touchStart, setTouchStart] = useState<{ x: number, y: number } | null>(null);
  const [touchCurrent, setTouchCurrent] = useState<{ x: number, y: number } | null>(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [userInteraction, setUserInteraction] = useState<'like' | 'dislike' | null>(null);
  /** Chave que incrementa a cada troca de slide para reiniciar animações */
  const [animKey, setAnimKey] = useState(0);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [viewport, setViewport] = useState({ width: 1920, height: 1080 });

  // ── Propaganda interstitial
  const [showAd, setShowAd] = useState(false);
  const [adProgress, setAdProgress] = useState(0);
  const [adAnimKey, setAdAnimKey] = useState(0);
  const [adSkippable, setAdSkippable] = useState(false);
  const contentsSinceAd = useRef(0);

  const currentItem = feed[currentIndex];

  useEffect(() => {
    if (!currentItem || !isPlaying) return;
    
    const durationMs = currentItem.duracaoSegundos * 1000;
    const interval = 50; 
    const step = (interval / durationMs) * 100;
    
    const timer = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          handleNext();
          return 0;
        }
        return prev + step;
      });
    }, interval);

    return () => clearInterval(timer);
  }, [currentIndex, currentItem, isPlaying]);

  useEffect(() => {
    setUserInteraction(null);
    setProgress(0);
    setAnimKey(k => k + 1);
  }, [currentIndex]);

  useEffect(() => {
    const updateViewport = () => {
      const w = rootRef.current?.clientWidth || window.innerWidth;
      const h = rootRef.current?.clientHeight || window.innerHeight;
      setViewport({ width: w, height: h });
    };
    updateViewport();
    window.addEventListener('resize', updateViewport);
    return () => window.removeEventListener('resize', updateViewport);
  }, []);

  function handleNext() {
    if (propagandaInterval > 0) {
      contentsSinceAd.current++;
      if (contentsSinceAd.current >= propagandaInterval) {
        contentsSinceAd.current = 0;
        setShowAd(true);
        setAdProgress(0);
        setAdSkippable(false);
        setAdAnimKey(k => k + 1);
        setIsPlaying(false);
      }
    }
    setCurrentIndex((prev) => (prev + 1) % feed.length);
  }

  function handlePrev() {
    setCurrentIndex((prev) => (prev - 1 + feed.length) % feed.length);
  }

  // Timer do interstitial de propaganda
  useEffect(() => {
    if (!showAd) return;
    const start = Date.now();
    const iv = setInterval(() => {
      const elapsed = Date.now() - start;
      const p = Math.min(100, (elapsed / AD_DURATION_MS) * 100);
      setAdProgress(p);
      if (elapsed > 3000) setAdSkippable(true);
      if (p >= 100) {
        clearInterval(iv);
        setShowAd(false);
        setAdProgress(0);
        setAdSkippable(false);
        setIsPlaying(true);
      }
    }, 50);
    return () => clearInterval(iv);
  }, [showAd]);

  const onTouchStart = (e: React.TouchEvent) => {
    const coords = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    setTouchStart(coords);
    setTouchCurrent(coords);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (!touchStart) return;
    setTouchCurrent({ x: e.touches[0].clientX, y: e.touches[0].clientY });
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchCurrent) return;
    const diffX = touchStart.x - touchCurrent.x;
    const diffY = touchStart.y - touchCurrent.y;
    
    if (Math.abs(diffX) > Math.abs(diffY)) {
      if (Math.abs(diffX) > 80) {
        if (diffX > 0) handleNext();
        else handlePrev();
      }
    } else {
      if (Math.abs(diffY) > 80) {
        if (diffY > 0) handleNext();
        else handlePrev();
      }
    }
    setTouchStart(null);
    setTouchCurrent(null);
  };

  const dragOffsetX = touchStart && touchCurrent ? touchCurrent.x - touchStart.x : 0;

  function handleAction(action: string, e?: React.MouseEvent) {
    if (e) e.stopPropagation();
    if (action === 'like') {
      setUserInteraction(prev => prev === 'like' ? null : 'like');
    } else if (action === 'dislike') {
      setUserInteraction(prev => prev === 'dislike' ? null : 'dislike');
    }
  }

  function handleCta(cta: MediaAcaoBotao, e: React.MouseEvent) {
    e.stopPropagation();
    if (cta.url) {
      window.open(cta.url, '_blank');
    } else {
      console.log('CTA action:', cta.texto);
    }
  }

  const togglePlay = () => setIsPlaying(prev => !prev);

  if (!currentItem) return null;

  // Helper para gerar animação fadeZoomBounce orquestrada com atraso
  const getAnim = (baseDelayIndex: number, animType: 'bounceZoom' | 'slideUp' | 'fadeIn' = 'bounceZoom') => {
    const duration = 0.6 * animationSpeedMultiplier;
    const delay = 0.2 + (baseDelayIndex * staggerDelay);
    const name = animType === 'bounceZoom' ? 'acmp-bounceZoom' : animType === 'slideUp' ? 'acm-slideUp' : 'acm-fadeIn';
    return {
      animation: `${name} ${duration}s cubic-bezier(0.34, 1.56, 0.64, 1) ${delay}s both`
    };
  };

  const getBadgeText = (nivel: string) => {
    if (nivel === 'ESTADO') return 'ESTADO DE MINAS GERAIS';
    if (nivel === 'MUNICIPIO') return 'PREFEITURA DE BAMBUÍ';
    if (nivel === 'ESCOLA') return 'E.E. JOÃO XXIII';
    return nivel;
  };

  const isFull = mode === 'full';
  const layoutScale = useMemo(() => {
    const baseW = 1920;
    const baseH = 1080;
    const scale = Math.min(viewport.width / baseW, viewport.height / baseH);
    return Math.max(0.55, Math.min(1.1, scale));
  }, [viewport.width, viewport.height]);

  const safeArea = useMemo(() => {
    const gutter = 24;
    const left = isFull ? Math.max(24, Math.min(56, 70 * layoutScale)) : Math.max(12, 22 * layoutScale);
    const desiredSidebarReserve = sidebarWidth != null ? sidebarWidth + gutter : gutter;
    /** Mantém faixa legível mesmo quando sidebar + gutter somam grande parte da largura */
    const minContentBand = Math.min(520, Math.max(260, viewport.width * 0.48));
    const maxRightByViewport = Math.max(gutter, viewport.width - left - minContentBand);
    const rightInset = isFull
      ? (sidebarWidth != null ? Math.min(desiredSidebarReserve, maxRightByViewport) : gutter)
      : 16;
    const topFull = Math.max(48, Math.min(Math.max(56, 165 * layoutScale), viewport.height * 0.14));
    const bottomFull = Math.max(36, Math.min(Math.max(44, 95 * layoutScale), viewport.height * 0.12));
    return {
      left,
      right: rightInset,
      top: isFull ? topFull : Math.max(40, Math.min(100, 115 * layoutScale)),
      bottom: isFull ? bottomFull : Math.max(22, Math.min(64, 52 * layoutScale)),
      /** Largura útil real do bloco de texto (padding já descontado da viewport do player) */
      contentMax: isFull
        ? Math.min(1240, Math.max(220, viewport.width - left - rightInset - 8))
        : Math.min(760, viewport.width * 0.9),
    };
  }, [isFull, layoutScale, sidebarWidth, viewport.width, viewport.height]);

  return (
    <div 
      ref={rootRef}
      onClick={!readonly ? togglePlay : undefined}
      onTouchStart={!readonly ? onTouchStart : undefined}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      style={{
        width: '100%', height: '100%', 
        background: '#0f172a', color: '#fff', position: 'relative', overflow: 'hidden',
        cursor: 'pointer'
      }}
    >
      
      {/* ══════ Imagem de Fundo (Ken Burns) ══════ */}
      {currentItem.imageUrl ? (
        <div key={`bg-${currentItem.id}`} style={{
          position: 'absolute', inset: -50,
          backgroundImage: `url(${currentItem.imageUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          animation: 'acm-kenBurns 20s infinite alternate ease-in-out',
          zIndex: 0
        }} />
      ) : (
        <div style={{ position: 'absolute', inset: 0, background: '#1e293b', zIndex: 0 }} />
      )}

      {/* ══════ Overlay Escuro ══════ */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'rgba(15,23,42,0.6)',
        backdropFilter: 'blur(8px)',
        zIndex: 1
      }} />
        
      {/* ══════ Badges Overlay (Origem + Etiqueta) — Conexão visual ══════ */}
      <div key={`badge-${animKey}`} style={{ position: 'absolute', top: isFull ? '4vh' : '24px', left: 0, display: 'flex', flexDirection: 'column', gap: '0.6vh', zIndex: 3 }}>
        {currentItem.tipo === 'PATROCINADO' && (
          <span style={{
            background: 'rgba(234,179,8,1)', color: '#000', padding: '0.8vh 1.2vw',
            borderRadius: '0 20px 20px 0', fontSize: 'clamp(0.7rem, 1.2vw, 0.9rem)',
            fontWeight: 800, display: 'inline-flex', alignItems: 'center', gap: 6, alignSelf: 'flex-start',
            animation: 'acm-slideFromRight 0.6s cubic-bezier(0.16, 1, 0.3, 1) both',
            animationDelay: '0.1s'
          }}>
            <Megaphone size={16} /> PATROCINADO: {currentItem.patrocinador}
          </span>
        )}
        {currentItem.nivel !== 'MASTER' && (
          <div style={{ display: 'flex', alignItems: 'stretch', fontFamily: 'Inter, sans-serif',
            ...getAnim(0, 'bounceZoom')
          }}>
            <span style={{
              background: 'rgba(255,255,255,0.1)', color: '#a78bfa',
              padding: '6px 14px', borderRadius: 8, fontSize: 'clamp(0.75rem, 1.4vw, 1rem)',
              fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em',
              border: '1px solid rgba(167,139,250,0.3)', backdropFilter: 'blur(8px)'
            }}>
              {getBadgeText(currentItem.nivel)}
            </span>
            {currentItem.tipo === 'ENQUETE' && (
              <span style={{
                background: '#3b82f6', color: '#fff', padding: '1.2vh 1.4vw',
                borderRadius: '0 24px 24px 0',
                fontSize: 'clamp(0.7rem, 1.2vw, 0.9rem)', fontWeight: 800,
                display: 'flex', alignItems: 'center',
              }}>
                ENQUETE ABERTA
              </span>
            )}
          </div>
        )}
        {currentItem.nivel === 'MASTER' && currentItem.tipo === 'ENQUETE' && (
          <span style={{
            background: '#3b82f6', color: '#fff', padding: '1vh 1.4vw',
            borderRadius: '0 24px 24px 0', fontSize: 'clamp(0.7rem, 1.2vw, 0.9rem)',
            fontWeight: 800, alignSelf: 'flex-start',
            animation: 'acm-slideFromRight 0.6s cubic-bezier(0.16, 1, 0.3, 1) both',
            animationDelay: '0.1s'
          }}>
            ENQUETE ABERTA
          </span>
        )}
      </div>

      {/* ══════ Controles: Prev / Play-Pause / Next (topo direito) ══════ */}
      {!readonly && (<div style={{
        position: 'absolute', top: isFull ? '4vh' : 24, right: isFull ? '2vw' : 16, zIndex: 10,
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <button
          onClick={(e) => { e.stopPropagation(); handlePrev(); }}
          style={{
            background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.2)', borderRadius: '50%',
            width: 42, height: 42, display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', cursor: 'pointer', transition: 'all 0.3s', opacity: 0.6,
          }}
          onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
          onMouseLeave={e => { e.currentTarget.style.opacity = '0.6'; }}
        >
          <ChevronLeft size={20} />
        </button>
        <button 
          onClick={(e) => { e.stopPropagation(); togglePlay(); }}
          style={{
            background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.2)', borderRadius: '50%',
            width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', cursor: 'pointer', transition: 'all 0.3s',
            boxShadow: isPlaying ? 'none' : '0 0 20px rgba(255,255,255,0.6)',
            opacity: isPlaying ? 0.4 : 1
          }}
        >
          {isPlaying ? <Pause size={22} /> : <Play size={22} fill="#fff" />}
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); handleNext(); }}
          style={{
            background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.2)', borderRadius: '50%',
            width: 42, height: 42, display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', cursor: 'pointer', transition: 'all 0.3s', opacity: 0.6,
          }}
          onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
          onMouseLeave={e => { e.currentTarget.style.opacity = '0.6'; }}
        >
          <ChevronRight size={20} />
        </button>
      </div>)}

      {/* ══════ Conteúdo Animado em Sequência Cinematográfica ══════ */}
      <div key={`content-${animKey}`} style={{
        position: 'relative', zIndex: 2,
        width: '100%', height: '100%',
        paddingTop: safeArea.top,
        paddingRight: safeArea.right,
        paddingBottom: safeArea.bottom,
        paddingLeft: safeArea.left,
        display: 'flex', flexDirection: 'column', justifyContent: 'flex-start',
        transform: `translateX(${dragOffsetX}px)`,
        transition: touchStart ? 'none' : 'transform 0.4s cubic-bezier(0.25, 1, 0.5, 1)'
      }}>
        
        {/* Etiqueta já está na barra de origem no topo */}

        {/* Etapa 2: Título — desce de cima, transparente → opaco (0.4s) */}
        <h1 style={{
          fontSize: isFull
            ? `clamp(${1.55 * layoutScale}rem, ${4.6 * layoutScale}vw, ${4.25 * layoutScale}rem)`
            : `clamp(${1.15 * layoutScale}rem, ${5.2 * layoutScale}vw, ${2.4 * layoutScale}rem)`,
          lineHeight: 1.15, textShadow: '0 4px 18px rgba(0,0,0,0.6)',
          marginBottom: `${Math.max(12, 28 * layoutScale)}px`,
          maxWidth: safeArea.contentMax,
          ...getAnim(1, 'bounceZoom'),
          overflow: 'hidden',
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          textWrap: 'balance',
        }}>
          {currentItem.titulo}
        </h1>
        
        {/* Etapa 3: Subtítulo — fade (0.8s) */}
        {currentItem.subtitulo && (
          <h2 style={{
            fontSize: isFull
              ? `clamp(${0.95 * layoutScale}rem, ${2.2 * layoutScale}vw, ${2 * layoutScale}rem)`
              : `clamp(${0.82 * layoutScale}rem, ${2.8 * layoutScale}vw, ${1.2 * layoutScale}rem)`,
            color: 'rgba(255, 255, 255, 0.8)', marginBottom: `${Math.max(12, 24 * layoutScale)}px`, fontWeight: 600,
            textShadow: '0 2px 12px rgba(0,0,0,0.6)',
            animation: 'acm-fadeIn 0.7s ease both', animationDelay: '0.8s', opacity: 0,
            maxWidth: safeArea.contentMax,
            overflow: 'hidden',
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            textWrap: 'balance',
          }}>
            {currentItem.subtitulo}
          </h2>
        )}

        {/* Etapa 4: Resumo — sobe de baixo (1.0s) */}
        {currentItem.resumo && (
          <p style={{
            fontSize: isFull
              ? `clamp(${0.92 * layoutScale}rem, ${1.65 * layoutScale}vw, ${1.35 * layoutScale}rem)`
              : `clamp(${0.86 * layoutScale}rem, ${3.4 * layoutScale}vw, ${1.05 * layoutScale}rem)`,
            color: '#e2e8f0', lineHeight: 1.55, marginBottom: `${Math.max(18, 36 * layoutScale)}px`,
            maxWidth: Math.min(safeArea.contentMax, viewport.width * 0.82),
            textShadow: '0 2px 10px rgba(0,0,0,0.5)',
            ...getAnim(2, 'bounceZoom'),
            overflow: 'hidden',
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
            display: '-webkit-box',
            WebkitLineClamp: isFull ? 5 : 4,
            WebkitBoxOrient: 'vertical',
          }}>
            {currentItem.resumo}
          </p>
        )}

        {/* Enquete Rendering */}
        {currentItem.enquete && (
          <div style={{
            display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 32,
            maxWidth: Math.min(600, safeArea.contentMax),
            width: '100%',
            animation: 'acm-slideUp 0.7s ease both', animationDelay: '1.2s', opacity: 0
          }}>
            {currentItem.enquete.opcoes.map(op => {
              const perc = Math.round((op.votos / currentItem.enquete!.totalVotos) * 100);
              return (
                <div key={op.id} onClick={(e) => e.stopPropagation()} style={{ position: 'relative', background: 'rgba(255,255,255,0.1)', padding: '16px 20px', borderRadius: 12, overflow: 'hidden', cursor: 'pointer', border: '1px solid rgba(255,255,255,0.2)' }}>
                  <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${perc}%`, background: 'rgba(59,130,246,0.5)', zIndex: 1, transition: 'width 1s ease' }} />
                  <div style={{ position: 'relative', zIndex: 2, display: 'flex', justifyContent: 'space-between', fontSize: '1.1rem' }}>
                    <span style={{ fontWeight: 600 }}>{op.texto}</span>
                    <span style={{ fontWeight: 800 }}>{perc}%</span>
                  </div>
                </div>
              );
            })}
            <p style={{ fontSize: '0.9rem', color: '#94a3b8', textAlign: 'right', fontWeight: 600 }}>TOTAL DE VOTOS: {currentItem.enquete.totalVotos}</p>
          </div>
        )}
      </div>

      {/* ══════ Barra Fixa Inferior: Fonte + Reações + CTA ══════ */}
      {!readonly && (
        <div key={`bottom-${animKey}`} style={{
          position: 'absolute', bottom: isFull ? '6vh' : 28, left: 0, right: 0, zIndex: 10,
          paddingLeft: isFull ? '4vw' : 16,
          paddingRight: isFull ? '4vw' : 16,
          display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
          ...getAnim(3, 'slideUp')
        }}>
          {/* Esquerda: Fonte + Like/Dislike */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1vh' }}>
          <p style={{ fontSize: 'clamp(0.65rem, 1.1vw, 0.85rem)', color: '#cbd5e1', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', margin: 0 }}>
            FONTE: {currentItem.fonte}
          </p>
          <div style={{ display: 'flex', gap: '0.8vw' }}>
            <button 
              onClick={(e) => handleAction('like', e)} 
              style={{ 
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '1.2vh 1.5vw', minWidth: 48, minHeight: 42,
                background: userInteraction === 'like' ? '#3b82f6' : 'rgba(255,255,255,0.1)', 
                border: '1px solid', borderColor: userInteraction === 'like' ? '#3b82f6' : 'rgba(255,255,255,0.2)', 
                borderRadius: 12, color: '#fff', cursor: 'pointer',
                transition: 'all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)', backdropFilter: 'blur(4px)',
                transform: userInteraction === 'like' ? 'scale(1.15)' : 'scale(1)'
              }} 
              onMouseEnter={e => { if(!userInteraction || userInteraction !== 'like') e.currentTarget.style.transform = 'scale(1.1)'; }}
              onMouseLeave={e => { if(!userInteraction || userInteraction !== 'like') e.currentTarget.style.transform = 'scale(1)'; }}
            >
              <ThumbsUp size={20} fill={userInteraction === 'like' ? '#fff' : 'none'} />
            </button>
            <button 
              onClick={(e) => handleAction('dislike', e)} 
              style={{ 
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '1.2vh 1.5vw', minWidth: 48, minHeight: 42,
                background: userInteraction === 'dislike' ? '#ef4444' : 'rgba(255,255,255,0.1)', 
                border: '1px solid', borderColor: userInteraction === 'dislike' ? '#ef4444' : 'rgba(255,255,255,0.2)', 
                borderRadius: 12, color: '#fff', cursor: 'pointer',
                transition: 'all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)', backdropFilter: 'blur(4px)',
                transform: userInteraction === 'dislike' ? 'scale(1.15)' : 'scale(1)'
              }} 
              onMouseEnter={e => { if(!userInteraction || userInteraction !== 'dislike') e.currentTarget.style.transform = 'scale(1.1)'; }}
              onMouseLeave={e => { if(!userInteraction || userInteraction !== 'dislike') e.currentTarget.style.transform = 'scale(1)'; }}
            >
              <ThumbsDown size={20} fill={userInteraction === 'dislike' ? '#fff' : 'none'} />
            </button>
          </div>
        </div>

        {/* Direita: Botão CTA */}
        {currentItem.acaoBotao && (
          <button
            onClick={(e) => handleCta(currentItem.acaoBotao!, e)}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: isFull ? '1.6vh 2.2vw' : '12px 20px',
              background: currentItem.acaoBotao.cor || '#10b981',
              border: 'none', borderRadius: 16, color: '#fff', cursor: 'pointer',
              fontWeight: 800, fontSize: 'clamp(0.8rem, 1.5vw, 1.1rem)',
              letterSpacing: '.03em',
              boxShadow: `0 8px 32px ${currentItem.acaoBotao.cor || '#10b981'}66`,
              backdropFilter: 'blur(4px)',
              animation: 'acm-ctaPop 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) both',
              animationDelay: '1.6s', opacity: 0,
              transition: 'transform 0.2s, box-shadow 0.2s',
              flexShrink: 0
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.08)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
          >
            {currentItem.acaoBotao.icone && CTA_ICONS[currentItem.acaoBotao.icone]}
            {currentItem.acaoBotao.texto}
          </button>
        )}
      </div>
      )}

      {/* ══════ Progress Bar Universal ══════ */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 6, background: 'rgba(255,255,255,0.1)', zIndex: 10 }}>
        <div style={{ height: '100%', background: '#3b82f6', width: `${progress}%`, transition: 'width 0.1s linear' }} />
      </div>

      {/* Exit button for dev mode */}
      {onExit && (
        <button onClick={(e) => { e.stopPropagation(); onExit(); }} style={{ position: 'absolute', top: 40, right: 100 + (sidebarWidth || 0), background: 'rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.2)', padding: '10px 20px', color: '#fff', borderRadius: 8, cursor: 'pointer', zIndex: 10, fontWeight: 700, backdropFilter: 'blur(4px)' }}>
          Sair da TV
        </button>
      )}

      {/* ══════ Propaganda Interstitial ══════ */}
      {showAd && (
        <div key={`ad-${adAnimKey}`} style={{
          position: 'absolute', inset: 0, zIndex: 50,
          background: 'linear-gradient(-45deg, #0f0a1a, #1a1035, #0d1b2a, #151025)',
          backgroundSize: '400% 400%',
          animation: 'acm-panBg 12s ease infinite',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          fontFamily: 'Inter, system-ui, sans-serif',
        }}>
          {/* Glow ambiental */}
          <div style={{
            position: 'absolute', width: 480, height: 480, borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(167,139,250,0.18) 0%, transparent 70%)',
            filter: 'blur(55px)', pointerEvents: 'none',
            animation: 'acm-glowIn 1.8s ease both',
          }} />

          {/* Escudo */}
          <div style={{
            width: 120, height: 120, marginBottom: 28,
            filter: 'drop-shadow(0 0 28px rgba(167,139,250,0.5))',
            animation: 'acm-shieldPop 1.2s cubic-bezier(0.16, 1, 0.3, 1) both',
          }}>
            <AcShieldLogo />
          </div>

          {/* Título */}
          <div style={{ display: 'flex', gap: 14, marginBottom: 12, alignItems: 'baseline' }}>
            <span style={{
              fontSize: 'clamp(2.2rem, 5vw, 3.8rem)', fontWeight: 900, color: '#fff',
              animation: 'acm-ltrPop 0.6s cubic-bezier(0.34, 1.50, 0.64, 1) 0.5s both',
              letterSpacing: '-0.02em',
            }}>Aluno</span>
            <span style={{
              fontSize: 'clamp(2.2rem, 5vw, 3.8rem)', fontWeight: 900, color: '#a78bfa',
              animation: 'acm-ltrPop 0.6s cubic-bezier(0.34, 1.50, 0.64, 1) 0.85s both',
              letterSpacing: '-0.02em',
            }}>Conectado</span>
          </div>

          {/* Subtitulo */}
          <p style={{
            fontSize: 'clamp(1rem, 2.2vw, 1.6rem)', color: 'rgba(255,255,255,0.85)',
            fontWeight: 500, margin: 0, letterSpacing: '0.04em', textAlign: 'center',
            animation: 'acm-fadeIn 1.2s ease 1.4s both',
          }}>Plataforma de Acesso à Educação</p>

          {/* Linha decorativa */}
          <div style={{
            height: 2, width: '60%', marginTop: 24,
            background: 'linear-gradient(90deg, transparent, rgba(167,139,250,0.65), transparent)',
            borderRadius: 2,
            animation: 'acm-lineGrow 1.2s ease 1.8s both',
          }} />

          {/* Pular btn */}
          {adSkippable && (
            <button
              onClick={(e) => { e.stopPropagation(); setShowAd(false); setIsPlaying(true); }}
              style={{
                marginTop: 36, padding: '10px 28px', borderRadius: 20,
                background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
                color: 'rgba(255,255,255,0.8)', cursor: 'pointer', fontSize: '.85rem', fontWeight: 700,
                backdropFilter: 'blur(10px)', transition: 'all .2s',
                animation: 'acm-fadeIn 0.4s ease both',
              }}
              onMouseOver={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.2)'; e.currentTarget.style.color = '#fff'; }}
              onMouseOut={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = 'rgba(255,255,255,0.8)'; }}
            >
              Pular anúncio →
            </button>
          )}

          {/* Progress bar do interstitial */}
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0, height: 4,
            background: 'rgba(255,255,255,0.08)',
          }}>
            <div style={{
              height: '100%', width: `${adProgress}%`,
              background: 'linear-gradient(90deg, #7c3aed, #a78bfa)',
              transition: 'width 0.1s linear',
            }} />
          </div>

          {/* Contador */}
          <div style={{
            position: 'absolute', top: 20, right: 24,
            fontSize: '.78rem', color: 'rgba(255,255,255,0.45)', fontWeight: 600,
          }}>
            Anúncio • {Math.ceil(((100 - adProgress) / 100) * (AD_DURATION_MS / 1000))}s
          </div>
        </div>
      )}

      {/* ══════ Animações Cinematográficas ══════ */}
      <style>{`
        @keyframes acm-kenBurns {
          0% { transform: scale(1) translate(0, 0); }
          100% { transform: scale(1.1) translate(-1%, -1%); }
        }

        /* Badge desliza da direita para a esquerda */
        @keyframes acm-slideFromRight {
          from { opacity: 0; transform: translateX(120%); }
          to   { opacity: 1; transform: translateX(0); }
        }

        /* Título desce de cima, transparente → opaco */
        @keyframes acm-dropTitle {
          from { opacity: 0; transform: translateY(-40px); filter: blur(4px); }
          to   { opacity: 1; transform: translateY(0);     filter: blur(0); }
        }

        /* Fade simples */
        @keyframes acm-fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }

        /* Sobe de baixo */
        @keyframes acm-slideUp {
          from { opacity: 0; transform: translateY(30px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        /* Bot\u00e3o CTA pop (escala + fade) */
        @keyframes acm-ctaPop {
          from { opacity: 0; transform: scale(0.6) translateY(20px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }

        /* ══ Propaganda Interstitial ══ */
        @keyframes acm-panBg {
          0%,100% { background-position: 0% 50%; }
          50%      { background-position: 100% 50%; }
        }
        @keyframes acm-shieldPop {
          0%   { opacity: 0; transform: scale(0.05) rotate(-8deg); filter: blur(12px); }
          40%  { opacity: 1; transform: scale(1.18) rotate(2deg); filter: blur(0); }
          60%  { transform: scale(0.94) rotate(-1deg); }
          80%  { transform: scale(1.04); }
          100% { opacity: 1; transform: scale(1) rotate(0deg); filter: none; }
        }
        @keyframes acm-ltrPop {
          from { opacity: 0; transform: translateY(24px) scale(0.8); filter: blur(4px); }
          to   { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
        }
        @keyframes acm-glowIn {
          from { opacity: 0; transform: translate(-50%, -50%) scale(0.5); }
          to   { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        }
        @keyframes acm-lineGrow {
          from { transform: scaleX(0); opacity: 0; }
          to   { transform: scaleX(1); opacity: 1; }
        }
        @keyframes acmp-bounceZoom {
          0% { transform: scale(0.6); opacity: 0; }
          60% { transform: scale(1.03); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
